import {
  Cushion,
  FlexContainer,
  Grid,
  MaxWidth,
  Rectangle,
  Shadow,
  Typography,
} from '@pitchero/react-ui';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import TopBannerVideoPlayerSwitch from '../../adverts/top-banner-video-player-switch';
import Article from '../../article';
import Comments from '../../comments/dynamic';
import Document from '../../document';
import SlashNavigation from '../../navigation/mobile/slash-navigation';
import SecondaryNavigationLinks from '../../navigation/secondary-navigation-links';
import RelatedContent from '../../related-content';
import VideoPlayer from '../../video/dynamic';
import { Trans } from '../../../lib/i18n';
import CLUB_PROP_TYPES from '../../../lib/prop-types/club';
import useBreakpoint from '../../../lib/use-breakpoint';
import NewsArticleLoadingState from './news-article-loading-state';
import NewsArticleQuote from './quote';
import Sidebar from '../sidebar';

const NewsArticle = ({
  author,
  body,
  club,
  documents,
  image,
  isLoading,
  tagline,
  title,
  shareProps,
  articleId,
  published,
  quote,
  quoteAuthor,
  sponsored,
  videos,
  renderSubheader,
  vimeoId,
}) => {
  const { advertisingTier, sportId } = club;
  const isMobile = useBreakpoint();
  const links = [
    {
      name: 'News',
      route: 'news',
      params: {},
      transKey: 'news:latest_news',
    },
    {
      name: 'Calendar',
      route: 'calendar',
      params: {},
      transKey: 'common:calendar',
    },
  ];

  const relatedContent = (
    <RelatedContent
      key={`news-${articleId}`}
      clubId={club.id}
      messageId={`${sponsored ? 'sponsored-content' : 'news'}-${articleId}`}
      placement={sponsored ? 'sponsored-content' : 'news_article'}
      includeMpu={!sponsored}
    />
  );

  return (
    <>
      {isMobile ? (
        <SlashNavigation
          name="News"
          nameTransKey="common:news_and_events"
          activeSection="news"
          activeSectionTransKey="news:latest_news"
          items={links}
        />
      ) : (
        <Shadow
          shadow="dark"
          component="div"
          style={{ position: 'relative', zIndex: 10, backgroundColor: '#fff' }}
        >
          <Cushion horizontal="large">
            <MaxWidth maxWidth={1366} center component="div">
              <Cushion vertical="medium" component="div">
                <FlexContainer alignItems="center">
                  <Typography preset="title" color="black" style={{ paddingRight: '20px' }}>
                    <Trans i18nKey="common:news_and_events">{title}</Trans>
                  </Typography>
                  <SecondaryNavigationLinks links={links} activeSection="News" />
                </FlexContainer>
              </Cushion>
            </MaxWidth>
          </Cushion>
        </Shadow>
      )}
      <Cushion
        horizontal="medium"
        vertical="medium"
        style={{ background: '#fff' }}
        responsive={[{ maxWidth: 'palm', props: { horizontal: 'small' } }]}
        component="div"
      >
        <MaxWidth component="div" maxWidth={1366} center>
          {!sponsored && (
            <Cushion bottom="medium">
              <TopBannerVideoPlayerSwitch
                advertisingTier={advertisingTier}
                pageType="news.article"
                sportId={sportId}
                uid={articleId.toString()}
              />
            </Cushion>
          )}
          <Grid
            className="grid-news-article"
            columns="1fr"
            columnGap="medium"
            responsive={[
              {
                minWidth: 'fullHeader',
                props: {
                  columns: 'minmax(auto, 970px) minmax(auto, 400px)',
                  areas: `'content sidebar'`,
                },
              },
            ]}
          >
            <Cushion
              component="div"
              bottom="medium"
              responsive={[{ maxWidth: 'lap', props: { bottom: '0px' } }]}
            >
              {isLoading ? (
                <NewsArticleLoadingState />
              ) : (
                <Article
                  imageAlt={title}
                  imageSrc={image}
                  title={title}
                  author={author}
                  tagline={tagline}
                  club={club}
                  showHeaderImage
                  shareProps={shareProps}
                  published={published}
                  sponsored={sponsored}
                  renderSubheader={renderSubheader}
                  vimeoId={vimeoId}
                  videoUrl={!isEmpty(videos) ? videos[0].url : null}
                >
                  {quote && <NewsArticleQuote author={quoteAuthor} body={quote} />}
                  <div className="bbcode-content" dangerouslySetInnerHTML={{ __html: body }} />
                  <div style={{ clear: 'both' }} />
                  {!isEmpty(videos) &&
                    videos.slice(1).map((video) => {
                      const videoOptions = {
                        autoplay: false,
                        controls: true,
                        aspectRatio: '16:9',
                        poster: video.thumbnail,
                        videoUrl: video.url,
                      };

                      return (
                        <Cushion vertical="medium" key={`videoPlayer${video.id}`}>
                          <VideoPlayer videoOptions={videoOptions} />
                        </Cushion>
                      );
                    })}
                  {!isEmpty(documents) && (
                    <Rectangle fill="seashellgrey">
                      <Grid className="ie-grid-three-col" preset="documents">
                        {documents.map((document) => (
                          <Cushion horizontal="small">
                            <Document document={document} key={document.id} />
                          </Cushion>
                        ))}
                      </Grid>
                    </Rectangle>
                  )}
                  <Comments club={club} mediaType="news" mediaId={articleId} />
                </Article>
              )}
            </Cushion>
            <Sidebar articleId={articleId} club={club} />
          </Grid>
          {relatedContent &&
            (isMobile ? (
              <Cushion horizontal="small" component="div">
                {relatedContent}
              </Cushion>
            ) : (
              <Rectangle
                borderTop="1px solid #d9d9d9"
                style={{ paddingTop: '30px', marginTop: '20px' }}
                component="div"
              >
                {relatedContent}
              </Rectangle>
            ))}
        </MaxWidth>
      </Cushion>
    </>
  );
};

NewsArticle.defaultProps = {
  author: null,
  body: null,
  documents: null,
  image: null,
  tagline: null,
  shareProps: null,
  published: null,
  quote: null,
  quoteAuthor: null,
  sponsored: false,
  renderSubheader: () => {},
  videos: null,
  vimeoId: null,
};

NewsArticle.propTypes = {
  author: PropTypes.shape({
    name: PropTypes.string,
  }),
  body: PropTypes.string,
  club: CLUB_PROP_TYPES.isRequired,
  documents: PropTypes.arrayOf(PropTypes.shape()),
  image: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  tagline: PropTypes.string,
  articleId: PropTypes.number.isRequired,
  shareProps: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  published: PropTypes.string,
  quote: PropTypes.string,
  quoteAuthor: PropTypes.string,
  sponsored: PropTypes.bool,
  renderSubheader: PropTypes.func,
  videos: PropTypes.arrayOf(PropTypes.shape()),
  vimeoId: PropTypes.string,
};

export default NewsArticle;
